/**
 * DECLINED CARD — one try, then a plain next step (#1984, card-hold step 1;
 * Corbin approved 2026-10-24, mockup Hq93GUWq3pbxXLACELEYB9).
 *
 * One customer's card was declined five times on the same $119.69 invoice,
 * because every screen offered Retry. A card now gets ONE try per invoice.
 * After a decline the office sees what the bank said, in plain words, and
 * what to do instead:
 *
 *   [Send a new card link]  [Collect on site]  [Customer says the card is fixed]
 *
 * "Customer says the card is fixed" buys exactly ONE more try (the server
 * enforces it — chargeGuard.ts). Lost / stolen / fraud: no more tries, ever,
 * so that button isn't there. A new card on file is always allowed.
 *
 * Used by Close Out's Charge card step, the invoice page's Charge card panel,
 * and the failed-payments list (Money → Payments).
 */
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, Check, CreditCard, Loader2, MapPin, Send, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HowItWorks, type HowStep } from "@/components/how-it-works";
import { cn } from "@/lib/utils";

/** The server's ChargeGuard (src/server/services/chargeGuard.ts). */
export type CardGuard =
    | { state: "clear" }
    | { state: "one_more_try"; failedPaymentId: string; reason: string; declinedAt: string }
    | {
          state: "blocked";
          failedPaymentId: string;
          reason: string;
          declinedAt: string;
          neverRetry: boolean;
          canMarkFixed: boolean;
          retryUsed: boolean;
          nextStep: string | null;
          message: string;
      };

/** The server refused a charge because the card was already declined. */
export function guardFromRefusal(err: unknown): CardGuard | null {
    if (!(err instanceof ApiError)) return null;
    const body = err.body as { blocked?: boolean; guard?: CardGuard } | null;
    if (err.status === 409 && body?.blocked && body.guard) return body.guard;
    if (err.status === 402 && body?.guard && body.guard.state !== "clear") return body.guard;
    return null;
}

export const DECLINED_CARD_HOW_STEPS: HowStep[] = [
    {
        title: "A card gets one try",
        text: "If the bank says no, GWC won't run that card again. Trying again almost never works, and the card companies fine businesses that keep trying.",
        picture: "declined-card-box.png",
        alt: "The red declined-card box, with the bank's answer ringed",
    },
    {
        title: "Pick a next step",
        text: (
            <>
                <b>Send a new card link</b> texts or emails the customer a link to save a different card. When they save
                it, Charge card works again. <b>Collect on site</b> marks it so you get paid in person; use Mark Paid
                when the money comes in.
            </>
        ),
        picture: "declined-card-next-steps.png",
        alt: "The Send a new card link and Collect on site buttons ringed",
    },
    {
        title: "Customer says it's fixed? One more try",
        text: 'If the customer called their bank and says the card works now, tap "Customer says the card is fixed." You get one more try. If that one is declined too, ask for a different card.',
        picture: "declined-card-fixed.png",
        alt: "The Customer says the card is fixed button ringed",
    },
    {
        title: "Lost, stolen or fraud: never again",
        text: "If the bank says the card was reported lost or stolen, or flagged it for fraud, there is no try-again button. Ask for a different card.",
    },
];

function stepNote(nextStep: string | null): string | null {
    if (nextStep === "card_link_sent")
        return "A new card link was sent. When the customer saves the card, you can charge it.";
    if (nextStep === "collect_on_site") return "Collecting on site. When you get paid, use Mark Paid.";
    return null;
}

export function DeclinedCardNextSteps({
    guard,
    last4,
    customerId,
    onChanged,
    onTryOnceMore,
    tryPending,
    className,
}: {
    guard: Exclude<CardGuard, { state: "clear" }>;
    last4: string | null;
    customerId: string | null;
    /** Refetch after a next step is recorded. */
    onChanged?: () => void;
    /** The one more try (shown only after "fixed" is marked). */
    onTryOnceMore?: () => void;
    tryPending?: boolean;
    className?: string;
}) {
    const card = last4 ? `card ending ${last4}` : "card";

    const nextStep = useMutation({
        mutationFn: (step: "card_link_sent" | "collect_on_site") =>
            api.post(`/api/payments/failed/${guard.failedPaymentId}/next-step`, { step }),
        onSuccess: () => onChanged?.(),
        onError: () => toast.error("Couldn't save that. Try again."),
    });

    const sendLink = useMutation({
        mutationFn: async () => {
            if (!customerId) throw new Error("no customer");
            const r = await api.post<{ sent: { sms: boolean; email: boolean } }>(
                `/api/customers/${customerId}/send-card-link`,
                {},
            );
            await api.post(`/api/payments/failed/${guard.failedPaymentId}/next-step`, { step: "card_link_sent" });
            return r;
        },
        onSuccess: (r) => {
            const ch = [r.sent.sms && "text", r.sent.email && "email"].filter(Boolean).join(" and ");
            if (ch) toast.success(`New card link sent by ${ch}.`);
            else toast.error("The link was made, but no text or email went out. Check the customer's phone and email.");
            onChanged?.();
        },
        onError: () => toast.error("Couldn't send the card link."),
    });

    const markFixed = useMutation({
        mutationFn: () => api.post(`/api/payments/failed/${guard.failedPaymentId}/card-fixed`, {}),
        onSuccess: () => {
            toast.success("Marked fixed. You get one more try.");
            onChanged?.();
        },
        onError: (e) => {
            const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
            toast.error(body?.message ?? "Couldn't mark it fixed.");
            onChanged?.();
        },
    });

    const busy = nextStep.isPending || sendLink.isPending || markFixed.isPending;

    if (guard.state === "one_more_try") {
        return (
            <div
                className={cn(
                    "space-y-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100",
                    className,
                )}
                data-testid="declined-card-one-more-try"
            >
                <div className="flex items-center gap-2">
                    <p className="flex min-w-0 items-center gap-2 font-semibold">
                        <Wrench className="h-4 w-4 shrink-0" />
                        The customer says the card is fixed. You get one more try.
                    </p>
                    <HowItWorks
                        title="How declined cards work"
                        steps={DECLINED_CARD_HOW_STEPS}
                        testId="how-declined-card"
                    />
                </div>
                <p>Last time: {guard.reason}</p>
                {onTryOnceMore && (
                    <Button size="sm" onClick={onTryOnceMore} disabled={tryPending} data-testid="try-card-once-more">
                        {tryPending ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <CreditCard className="mr-1 h-3.5 w-3.5" />
                        )}
                        Try the card once more
                    </Button>
                )}
            </div>
        );
    }

    const note = stepNote(guard.nextStep);
    return (
        <div
            className={cn(
                "space-y-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200",
                className,
            )}
            role="alert"
            data-testid="declined-card-next-steps"
        >
            <div className="flex items-center gap-2">
                <p className="flex min-w-0 items-center gap-2 font-semibold">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    The {card} was declined. Nothing was charged.
                </p>
                <HowItWorks
                    title="How declined cards work"
                    steps={DECLINED_CARD_HOW_STEPS}
                    testId="how-declined-card"
                />
            </div>
            <p data-testid="declined-card-reason">{guard.reason}</p>
            <p className="font-medium">
                {guard.neverRetry
                    ? "We won't run this card again. Ask the customer for a different card."
                    : guard.retryUsed
                      ? "We tried once more after the customer said it was fixed. It was declined again. Ask for a different card."
                      : "We won't try this card again."}
            </p>
            {note && (
                <p className="flex items-center gap-1.5" data-testid="declined-card-step-note">
                    <Check className="h-4 w-4 shrink-0" />
                    {note}
                </p>
            )}
            <div className="flex flex-wrap gap-2 pt-0.5">
                {customerId && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="bg-background"
                        disabled={busy}
                        onClick={() => sendLink.mutate()}
                        data-testid="declined-send-card-link"
                    >
                        {sendLink.isPending ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Send className="mr-1 h-3.5 w-3.5" />
                        )}
                        Send a new card link
                    </Button>
                )}
                <Button
                    size="sm"
                    variant="outline"
                    className="bg-background"
                    disabled={busy || guard.nextStep === "collect_on_site"}
                    onClick={() => nextStep.mutate("collect_on_site")}
                    data-testid="declined-collect-on-site"
                >
                    {nextStep.isPending ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <MapPin className="mr-1 h-3.5 w-3.5" />
                    )}
                    Collect on site
                </Button>
                {guard.canMarkFixed && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="bg-background"
                        disabled={busy}
                        onClick={() => markFixed.mutate()}
                        data-testid="declined-card-fixed"
                    >
                        {markFixed.isPending ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Wrench className="mr-1 h-3.5 w-3.5" />
                        )}
                        Customer says the card is fixed
                    </Button>
                )}
            </div>
        </div>
    );
}
